import type { RfqTabState } from '@forgd/liquidity.forgd.com/layers/rfq/composables/useRfqTabState'
import type { MarketMakerRequestStatusEnumType } from '@forgd/shared/enums'
import type { RfqRound } from '@forgd/shared/rfq/types'
import { buildApiQuery } from '@forgd/app-layer-core/composables/useApiQuery'
import { getOutcomeTabState, getQuoteTabState } from '@forgd/liquidity.forgd.com/layers/rfq/composables/useRfqTabState'
import { MarketMakerRequestStatusEnum as S } from '@forgd/shared/enums'
import { useQueryCache } from '@pinia/colada'

type TabType = 'quotes' | 'outcome'

const TAB_ROUTE_PATTERN = /^round-([12])-(my-quotes|outcome)$/

function parseTabRoute(segment: string): { type: TabType, round: RfqRound } | null {
  const match = segment.match(TAB_ROUTE_PATTERN)
  if (!match) {
    return null
  }
  return {
    round: Number(match[1]) as RfqRound,
    type: match[2] === 'my-quotes' ? 'quotes' : 'outcome',
  }
}

function getTabState(round: RfqRound, type: TabType, status: MarketMakerRequestStatusEnumType | undefined, isReopenedForMe?: boolean, currentRound?: number): RfqTabState {
  if (round === 1) {
    return type === 'quotes'
      ? getQuoteTabState(status, S.RoundOneWaitingForQuotes, S.RoundOneQuotesInReview, 1, isReopenedForMe, currentRound)
      : getOutcomeTabState(status, S.RoundOneQuotesAccepted, S.RoundOneWaitingForQuotes)
  }
  return type === 'quotes'
    ? getQuoteTabState(status, S.RoundTwoWaitingForQuotes, S.RoundTwoQuotesInReview, 2, isReopenedForMe, currentRound)
    : getOutcomeTabState(status, S.RoundTwoQuotesAccepted, S.RoundTwoWaitingForQuotes)
}

export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) {
    return
  }

  const routeSegment = to.path.split('/').filter(Boolean).at(-1) || ''
  const tab = parseTabRoute(routeSegment)
  if (!tab) {
    return
  }

  const rfqId = to.params.rfqId as string
  if (!rfqId) {
    return
  }

  try {
    const queryCache = useQueryCache()
    const queryOptions = buildApiQuery('/rfq/mm/rfq-details', {
      params: () => ({
        query: { id: rfqId },
      }),
    })

    const entry = queryCache.ensure(queryOptions)
    const result = await queryCache.fetch(entry)

    const details = result.data
    const status = details?.rfq?.status
    const isReopenedForMe = details?.rfq?.isReopenedForMe
    const currentRound = details?.rfq?.round

    const tabState = getTabState(tab.round, tab.type, status, isReopenedForMe, currentRound)

    if (tabState === 'disabled' || tabState === 'locked') {
      return showError({ statusCode: 404, statusMessage: 'Page not found' })
    }
  }
  catch (error: unknown) {
    const statusCode = (error as { statusCode?: number })?.statusCode
      ?? (error as { status?: number })?.status
    if (statusCode === 404) {
      return showError({ statusCode: 404, statusMessage: 'Page not found' })
    }
    throw error
  }
})
